"use client";
import React from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Link from "next/link";
import PageTransition from "@/components/animations/PageTransition";
import FadeInView from "@/components/animations/FadeInView";
import StaggerContainer from "@/components/animations/StaggerContainer";
import Breadcrumb from "@/components/Breadcrumb";

const perks = [
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      </svg>
    ),
    title: "Cutting-Edge Tech",
    desc: "Work with the latest in cloud, cybersecurity, networking and AI — across real business environments.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    title: "Small Team, Big Impact",
    desc: "No corporate red tape. Your ideas matter and you'll see the direct impact of your work every day.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="3" width="20" height="14" rx="2" />
        <line x1="8" y1="21" x2="16" y2="21" />
        <line x1="12" y1="17" x2="12" y2="21" />
      </svg>
    ),
    title: "Flexible Working",
    desc: "Hybrid work options with a modern Auckland office. We trust our team to deliver results.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
      </svg>
    ),
    title: "Growth & Development",
    desc: "Microsoft certifications, vendor training, conferences — we invest in your career progression.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" />
        <path d="M8 14s1.5 2 4 2 4-2 4-2" />
        <line x1="9" y1="9" x2="9.01" y2="9" />
        <line x1="15" y1="9" x2="15.01" y2="9" />
      </svg>
    ),
    title: "Great Culture",
    desc: "Team lunches, social events, and a genuinely supportive environment where people enjoy coming to work.",
  },
  {
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <line x1="12" y1="1" x2="12" y2="23" />
        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
      </svg>
    ),
    title: "Competitive Pay",
    desc: "Market-rate salaries, performance bonuses, and regular salary reviews as you grow with us.",
  },
];

const values = [
  { title: "Client First", desc: "Everything we do is about making our clients' businesses run better." },
  { title: "Zero Geek Speak", desc: "We explain technology in plain language — no jargon, no confusion." },
  { title: "Own It", desc: "We take ownership of problems and see them through to resolution." },
  { title: "Always Learning", desc: "Technology never stops evolving — neither do we." },
];

export default function CareersPage() {
  return (
    <PageTransition>
      <main style={{ background: "#070B14", overflowX: "hidden" }}>
        <Header />
        <Breadcrumb
          items={[
            { label: "Home", href: "/" },
            { label: "Company", href: "/company" },
            { label: "Careers" },
          ]}
        />

        {/* Hero */}
        <section className="relative min-h-[320px] md:min-h-[480px] flex items-center justify-center py-32 overflow-hidden">
          <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, #070B14 0%, #0B1220 40%, #0f1f3d 70%, #070B14 100%)" }} />
          <div className="absolute top-1/4 right-1/4 w-96 h-96 rounded-full blob-1" style={{ background: "radial-gradient(circle, rgba(37,99,235,0.15) 0%, transparent 70%)" }} />
          <div className="absolute bottom-1/4 left-1/4 w-80 h-80 rounded-full blob-2" style={{ background: "radial-gradient(circle, rgba(34,211,238,0.08) 0%, transparent 70%)" }} />
          <div className="relative max-w-container mx-auto px-4 sm:px-6 text-center">
            <FadeInView delay={0}>
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-pill mb-8" style={{ background: "rgba(37,99,235,0.12)", border: "1px solid rgba(59,130,246,0.25)" }}>
                <span className="w-2 h-2 rounded-full bg-blue-400" />
                <span className="text-[11px] font-body font-600 uppercase tracking-widest gradient-text">Careers</span>
              </div>
            </FadeInView>
            <FadeInView delay={0.15}>
              <h1 className="font-heading font-800 text-display-md md:text-display mb-6" style={{ color: "#F8FAFC", letterSpacing: "-0.03em", lineHeight: "1.05" }}>
                Build Your Career in<br />
                <span className="gradient-text">New Zealand IT</span>
              </h1>
            </FadeInView>
            <FadeInView delay={0.3}>
              <p className="text-body-lg leading-[1.75] max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>
                Join a growing team of IT professionals who are passionate about helping New Zealand businesses succeed with technology.
              </p>
            </FadeInView>
          </div>
        </section>

        {/* Why Pro IT */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <div className="section-sep mx-auto mb-5" />
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Why Work at Pro IT Solutions?</h2>
              <p className="text-body-lg max-w-2xl mx-auto" style={{ color: "#94A3B8" }}>
                We&apos;re not a giant corporation — we&apos;re a tight-knit team that punches well above its weight.
              </p>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 items-stretch" staggerDelay={0.1}>
              {perks.map((perk) => (
                <div
                  key={perk.title}
                  className="rounded-card-lg p-5 sm:p-8 h-full flex flex-col"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-5" style={{ background: "rgba(59,130,246,0.1)", color: "#60A5FA" }}>
                    {perk.icon}
                  </div>
                  <h3 className="font-heading font-700 text-[20px] mb-3" style={{ color: "#F8FAFC" }}>{perk.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#94A3B8" }}>{perk.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Our Values */}
        <section className="py-20 lg:py-28" style={{ background: "rgba(255,255,255,0.02)" }}>
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Our Values</h2>
              <p className="text-body-lg max-w-xl mx-auto" style={{ color: "#94A3B8" }}>
                These aren&apos;t just words on a wall — they guide how we work every day.
              </p>
            </FadeInView>
            <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch" staggerDelay={0.1}>
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-card-lg p-6 h-full flex flex-col text-center"
                  style={{ background: "linear-gradient(135deg, rgba(37,99,235,0.1) 0%, rgba(37,99,235,0.03) 100%)", border: "1px solid rgba(59,130,246,0.2)" }}
                >
                  <h3 className="font-heading font-700 text-[18px] mb-3" style={{ color: "#F8FAFC" }}>{v.title}</h3>
                  <p className="text-[14px] font-body leading-[1.75]" style={{ color: "#CBD5E1" }}>{v.desc}</p>
                </div>
              ))}
            </StaggerContainer>
          </div>
        </section>

        {/* Open Positions */}
        <section className="py-20 lg:py-28">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView className="text-center mb-16">
              <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Open Positions</h2>
              <p className="text-body-lg max-w-xl mx-auto" style={{ color: "#94A3B8" }}>
                We&apos;re always looking for talented people. If you don&apos;t see a role that fits, send us your CV anyway.
              </p>
            </FadeInView>
            <FadeInView>
              <div
                className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)" }}
              >
                <div className="text-5xl mb-6">📬</div>
                <h3 className="font-heading font-700 text-[22px] mb-4" style={{ color: "#F8FAFC" }}>No current openings listed</h3>
                <p className="text-[15px] font-body leading-[1.75] max-w-lg mx-auto mb-8" style={{ color: "#94A3B8" }}>
                  We&apos;re growing fast and new roles come up regularly. Send your CV and a short intro to get on our radar for upcoming opportunities.
                </p>
                <a
                  href="mailto:careers@proitsolutions.nz"
                  className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary inline-flex items-center gap-2"
                >
                  Send Your CV
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </a>
              </div>
            </FadeInView>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="max-w-container mx-auto px-4 sm:px-6">
            <FadeInView scale={0.95}>
              <div className="rounded-card-lg p-6 sm:p-10 lg:p-16 text-center" style={{ background: "rgba(37,99,235,0.08)", border: "1px solid rgba(59,130,246,0.2)" }}>
                <h2 className="font-heading font-700 text-[24px] sm:text-[36px] leading-[1.05] mb-4" style={{ color: "#F8FAFC" }}>Know someone who&apos;d be a great fit?</h2>
                <p className="text-body-lg mb-8 leading-[1.75]" style={{ color: "#94A3B8" }}>Share this page or get in touch — we love referrals from our network.</p>
                <Link href="/contact" className="px-6 sm:px-8 py-3.5 sm:py-4 rounded-pill font-heading font-600 btn-primary">Get in Touch</Link>
              </div>
            </FadeInView>
          </div>
        </section>

        <Footer />
      </main>
    </PageTransition>
  );
}
